/**
 * Algolia InstantSearch (/search) — collection filter UX parity.
 * Collection chrome is injected after Algolia renders its default layout.
 */

const WF_SEARCH_DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');

/** @type {boolean} */
let wfSearchFacetsInitialized = false;

/** @type {string | null} */
let wfSearchFacetLayoutMode = null;

/** @type {boolean} */
let wfSearchInstantSearchReady = false;

/** @type {number | null} */
let wfSearchDrawerScrollY = null;

// Keep in sync with assets/icon-nav-arrow.svg
const SEARCH_DRILLDOWN_NAV_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="10" viewBox="0 0 24 10" fill="none" aria-hidden="true"><path d="M23.4596 5.24624C23.7135 4.9924 23.7135 4.58084 23.4596 4.327L19.323 0.190426C19.0692 -0.0634151 18.6576 -0.0634151 18.4038 0.190426C18.15 0.444266 18.15 0.855824 18.4038 1.10966L22.0808 4.78662L18.4038 8.46357C18.15 8.71742 18.15 9.12897 18.4038 9.38281C18.6576 9.63665 19.0692 9.63665 19.323 9.38281L23.4596 5.24624ZM0 4.78662L5.11067e-08 5.43662L23 5.43662L23 4.78662L23 4.13662L-5.11067e-08 4.13662L0 4.78662Z" fill="#C48C5D"/></svg>`;

// Keep in sync with assets/icon-caret.svg
const SEARCH_CARET_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// Keep in sync with assets/icon-close.svg
const SEARCH_CLOSE_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2L2 12" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// Back arrow of the PLP drawer panes (snippets/facets-drawer-body.liquid)
const SEARCH_DRILLDOWN_BACK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="10" viewBox="0 0 24 10" fill="none"><path d="M0.190283 5.24624C-0.0635586 4.9924 -0.0635586 4.58084 0.190283 4.327L4.32686 0.190426C4.5807 -0.0634151 4.99226 -0.0634151 5.2461 0.190426C5.49994 0.444266 5.49994 0.855824 5.2461 1.10966L1.56914 4.78662L5.2461 8.46357C5.49994 8.71742 5.49994 9.12897 5.2461 9.38281C4.99226 9.63665 4.5807 9.63665 4.32686 9.38281L0.190283 5.24624ZM23.6499 4.78662L23.6499 5.43662L0.649902 5.43662L0.649902 4.78662L0.649902 4.13662L23.6499 4.13662L23.6499 4.78662Z" fill="black"/></svg>`;

// Keep in sync with assets/icon-checkmark.svg
const SEARCH_CHECKMARK_SVG = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * @returns {unknown}
 */
function getInstantSearchHelper() {
  // @ts-expect-error Algolia app injects this global at runtime
  const search = algoliaShopify?.instantsearch?.search;
  return (
    search?.helper ??
    search?.mainHelper ??
    // @ts-expect-error Algolia app injects this global at runtime
    algoliaShopify?.instant?.search?.helper ??
    // @ts-expect-error Algolia app injects this global at runtime
    algoliaShopify?.search?.helper ??
    null
  );
}

/**
 * @returns {number}
 */
function getInstantSearchHitTotal() {
  const helper = /** @type {{ lastResults?: { nbHits?: number } } | null} */ (
    getInstantSearchHelper()
  );
  const nbHits = helper?.lastResults?.nbHits;
  if (typeof nbHits === 'number') return nbHits;

  const statsText =
    document.querySelector('.ais-stats-container')?.textContent ?? '';
  const match = statsText.match(/([\d,]+)\s+results found/i);
  if (match?.[1]) {
    return Number.parseInt(match[1].replace(/,/g, ''), 10);
  }

  return 0;
}

/**
 * The PLP prints its counts through Liquid's `t: count:`, which leaves the
 * number bare (2795, not 2,795), so these do the same.
 *
 * @param {number} count
 * @returns {string}
 */
function formatSearchProductCount(count) {
  const translations = window.Theme?.translations ?? {};
  const cutoff = Number(translations.search_products_cutoff_count) || 25000;

  if (count > cutoff) {
    return (
      translations.search_products_cutoff ?? `More than ${cutoff} Results`
    );
  }

  const template =
    count === 1
      ? (translations.search_products_count_one ?? '{{ count }} Result')
      : (translations.search_products_count_other ?? '{{ count }} Results');

  return template.replace('{{ count }}', String(count));
}

/**
 * The mobile pill says "Products" where the desktop bar says "Results" — the
 * PLP uses collection_grid_products_count there, so this does too.
 *
 * @param {number} count
 * @returns {string}
 */
function formatSearchPillCount(count) {
  const translations = window.Theme?.translations ?? {};
  const cutoff = Number(translations.search_products_cutoff_count) || 25000;

  if (count > cutoff) {
    return translations.search_pill_cutoff ?? `More than ${cutoff} Products`;
  }

  const template =
    count === 1
      ? (translations.search_pill_count_one ?? '{{ count }} Product')
      : (translations.search_pill_count_other ?? '{{ count }} Products');

  return template.replace('{{ count }}', String(count));
}

/**
 * @param {number} count
 * @returns {string}
 */
function formatSearchSeeItemsLabel(count) {
  const translations = window.Theme?.translations ?? {};
  const template =
    count === 1
      ? (translations.search_see_items_one ?? 'See {{ count }} item')
      : (translations.search_see_items_other ?? 'See {{ count }} items');

  return template.replace('{{ count }}', String(count));
}

/**
 * @param {Document | Element} root
 */
function updateSearchProductCounts(root) {
  const total = getInstantSearchHitTotal();
  const label = formatSearchProductCount(total);
  const seeItemsLabel = formatSearchSeeItemsLabel(total);

  root.querySelectorAll('.wf-search__products-count-text').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.textContent = label;
    }
  });

  const pillLabel = formatSearchPillCount(total);
  root.querySelectorAll('.wf-search__filter-pill-count').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.textContent = pillLabel;
    }
  });

  root.querySelectorAll('.wf-search__see-results').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.textContent = seeItemsLabel;
    }
  });
}

/**
 * @param {HTMLElement} page
 * @returns {HTMLElement | null}
 */
function getSearchResultsBlock(page) {
  const results = page.querySelector('.wf-search__results-block');
  if (results instanceof HTMLElement) return results;

  const block = page.querySelector('.ais-block');
  return block instanceof HTMLElement ? block : null;
}

/**
 * @param {HTMLElement} page
 */
function ensureSearchFacetChromePosition(page) {
  const chrome = page.querySelector('.wf-search__facets');
  const results = getSearchResultsBlock(page);

  if (
    !(chrome instanceof HTMLElement) ||
    !(results instanceof HTMLElement) ||
    chrome.nextElementSibling === results
  ) {
    return;
  }

  results.insertAdjacentElement('beforebegin', chrome);
}

/**
 * @param {HTMLElement} page
 */
function injectSearchFacetChrome(page) {
  if (page.querySelector('.wf-search__facets')) {
    ensureSearchFacetChromePosition(page);
    return;
  }

  const filterPillLabel =
    window.Theme?.translations?.search_filter_pill_label ??
    'Filter & Sort';
  const drawerTitle =
    window.Theme?.translations?.search_filter_drawer_title ?? 'Filter';
  const closeLabel = window.Theme?.translations?.search_close_label ?? 'Close';
  const clearAllLabel =
    window.Theme?.translations?.search_clear_all_label ?? 'Clear all';

  const chrome = document.createElement('div');
  chrome.className = 'wf-cg__facets wf-search__facets';
  chrome.innerHTML = `
    <div class="wf-cg__facets-sticky wf-search__facets-sticky">
      <div class="facets-block-wrapper facets-block-wrapper--horizontal wf-cg__facets-desktop wf-search__facets-desktop" aria-label="Filters">
        <div class="facets facets--horizontal wf-search__facets-horizontal">
          <div class="wf-search__facets-toolbar">
            <div class="wf-search__facets-list-wrap facets__filters-wrapper"></div>
            <div class="products-count-wrapper wf-search__products-count" data-testid="products-count">
              <span class="wf-search__products-count-text"></span>
            </div>
          </div>
        </div>
      </div>

      <div class="facets-toggle wf-cg__facets-toggle wf-search__facets-toggle">
        <div class="facets-toggle__wrapper wf-cg__facets-toggle-inner">
          <button type="button" class="button facets-toggle__button button-unstyled wf-cg__filter-pill-btn wf-search__filter-pill-btn" data-wf-search-open-filters>
            <span class="wf-cg__filter-pill wf-search__filter-pill">
              <span class="wf-cg__filter-pill-label wf-search__filter-pill-label">${filterPillLabel}</span>
              <span class="wf-cg__filter-pill-sep wf-search__filter-pill-sep" aria-hidden="true">-</span>
              <span class="wf-cg__filter-pill-count wf-search__filter-pill-count"></span>
            </span>
          </button>
        </div>
      </div>

      <div class="wf-cg__facet-remove-row wf-search__facet-remove-row" hidden>
        <div class="wf-search__refinement-chips facets-remove facets-remove--mobile-and-vertical"></div>
        <div class="facets-horizontal-remove wf-search__clear-all-wrap"></div>
      </div>

      <dialog-component id="wf-search-filters-drawer" class="facets-block-wrapper facets-block-wrapper--vertical wf-search__filters-drawer">
        <dialog ref="dialog" class="facets facets--drawer dialog-modal drawer dialog-drawer dialog-drawer--right spacing-style wf-cg__drawer-dialog wf-search__drawer-dialog" scroll-lock aria-labelledby="wf-search-filters-drawer-heading">
          <div class="facets__title-wrapper facets-drawer-drilldown__root-header wf-search__drawer-header">
            <h2 id="wf-search-filters-drawer-heading" class="facets-drawer__title facets-drawer-drilldown__root-title h3">${drawerTitle}</h2>
            <button type="button" class="button facets-drawer__close facets-drawer-drilldown__root-close button-unstyled close-button wf-search__drawer-close" data-wf-search-close-filters aria-label="${closeLabel}">
              <span class="svg-wrapper svg-wrapper--small" title="${closeLabel}">${SEARCH_CLOSE_SVG}</span>
            </button>
          </div>
          <scroll-hint class="facets-drawer__filters facets-drawer-drilldown__scroll wf-search__drawer-scroll">
            <balance-filters-drilldown class="facets-drawer-drilldown wf-search__drilldown">
              <div class="facets-drawer-drilldown__stack">
                <div class="facets-drawer-drilldown__root" data-filter-drilldown-root>
                  <nav class="facets-drawer-drilldown__root-nav wf-search__drilldown-root-nav" aria-label="Filters"></nav>
                </div>
                <div class="facets-drawer-drilldown__panes wf-search__drilldown-panes" data-filter-drilldown-panes aria-hidden="true"></div>
              </div>
            </balance-filters-drilldown>
          </scroll-hint>
          <div class="facets__drawer-actions facets-drawer-drilldown__actions wf-search__drawer-actions">
            <button type="button" class="button-secondary facets__clear-all wf-search__drawer-clear-all">
              ${clearAllLabel}
            </button>
            <button type="button" class="button facets__see-results facets-drawer-drilldown__see-results wf-search__see-results" data-wf-search-close-filters>
              See results
            </button>
          </div>
        </dialog>
      </dialog-component>
    </div>
  `;

  const resultsBlock = getSearchResultsBlock(page);
  if (resultsBlock instanceof HTMLElement) {
    resultsBlock.insertAdjacentElement('beforebegin', chrome);
  } else {
    page.appendChild(chrome);
  }

  ensureSearchFacetChromePosition(page);
}

/**
 * @returns {boolean}
 */
function searchFacetWidgetsAreMounted() {
  return [...document.querySelectorAll('.wf-search__facets-source [class*="-container"]')].some(
    (node) => node instanceof HTMLElement && node.children.length > 0,
  );
}

/**
 * @param {HTMLElement} chipsMount
 * @param {HTMLElement} clearMount
 * @param {HTMLElement} sourceRefined
 * @param {HTMLElement} sourceClear
 */
function syncSearchRefinementChips(
  chipsMount,
  clearMount,
  sourceRefined,
  sourceClear,
) {
  const refinedList = sourceRefined.querySelector(
    '.ais-current-refined-values--list',
  );
  const hasRefinements =
    refinedList instanceof HTMLElement && refinedList.children.length > 0;

  chipsMount.innerHTML = '';

  if (refinedList instanceof HTMLElement) {
    refinedList.querySelectorAll('.ais-current-refined-values--link').forEach(
      (link) => {
        if (!(link instanceof HTMLAnchorElement)) return;

        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className =
          'pills__pill pills__pill--desktop-small facets-remove__pill wf-search__refinement-pill button-unstyled';
        pill.textContent = link.textContent?.trim() ?? '';
        pill.addEventListener('click', (event) => {
          event.preventDefault();
          link.click();
        });
        chipsMount.appendChild(pill);
      },
    );
  }

  clearMount.innerHTML = '';
  const clearButton = sourceClear.querySelector(
    '.ais-ClearRefinements-button:not(.ais-ClearRefinements-button--disabled)',
  );

  if (clearButton instanceof HTMLButtonElement) {
    const wrap = document.createElement('facet-remove-component');
    wrap.className =
      'facets-horizontal-remove facets-horizontal-remove--active wf-search__clear-all';

    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'button-unstyled facets__clear-all-link facets__clear-all-link--horizontal facets__clear-all-link--active';
    button.textContent = clearButton.textContent?.trim() ?? 'Clear all';
    button.addEventListener('click', () => {
      clearButton.click();
    });

    wrap.appendChild(button);
    clearMount.appendChild(wrap);
  }

  const row = chipsMount.closest('.wf-search__facet-remove-row');
  if (row instanceof HTMLElement) {
    row.hidden = !hasRefinements;
  }

  // The drawer footer's Clear all, shown only while something is applied —
  // the PLP toggles the same facets__clear-all--active class.
  const page = chipsMount.closest('.ais-page');
  const drawerClear = page?.querySelector('.wf-search__drawer-clear-all');
  if (drawerClear instanceof HTMLElement) {
    drawerClear.classList.toggle(
      'facets__clear-all--active',
      clearButton instanceof HTMLButtonElement,
    );
  }
}

/**
 * @param {HTMLElement} wrapper
 * @param {string} paneId
 */
function buildMobileDrilldownPaneHeader(wrapper, paneId) {
  const title =
    wrapper.querySelector('.ais-facet--header .facets__label')?.textContent?.trim() ??
    wrapper.querySelector('.ais-facet--header')?.textContent?.trim() ??
    'Filter';

  const header = document.createElement('header');
  header.className =
    'facets-drawer-drilldown__pane-header wf-search__drawer-pane-header';

  // As on the PLP: back arrow and title only. The drawer's own × stays in
  // view above, so a second close here would read as "close this filter".
  header.innerHTML = `
    <div class="facets-drawer-drilldown__pane-header-main">
      <button type="button" class="facets-drawer-drilldown__pane-back button-unstyled" data-filter-drilldown-back aria-label="${window.Theme?.translations?.search_back_label ?? 'Back'}">
        <span class="svg-wrapper" aria-hidden="true">${SEARCH_DRILLDOWN_BACK_SVG}</span>
      </button>
      <span class="facets-drawer-drilldown__pane-title">${title}</span>
    </div>
  `;

  const pane = document.createElement('div');
  pane.id = paneId;
  pane.className = 'facets-drawer-drilldown__pane wf-search__drawer-pane';
  pane.setAttribute('aria-hidden', 'true');

  const body = document.createElement('div');
  body.className =
    'facets-drawer-drilldown__pane-body wf-search__drawer-pane-body';

  pane.appendChild(header);
  body.appendChild(wrapper);
  pane.appendChild(body);

  return pane;
}

/**
 * @param {HTMLElement} facetsSource
 * @param {HTMLElement} listWrap
 */
function ensureDesktopFacetPlacement(facetsSource, listWrap) {
  facetsSource.classList.remove('wf-search__facets-source--drawer');
  facetsSource.classList.add('wf-search__facets-source--desktop');

  if (facetsSource.parentElement !== listWrap) {
    listWrap.appendChild(facetsSource);
  }

  facetsSource.querySelectorAll('.ais-facet-dropdown-wrapper').forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.hidden = false;
    const trigger = wrapper.querySelector('.wf-search__facet-trigger');
    const checkbox = wrapper.querySelector('.ais-dropdown-checkbox');
    if (
      trigger instanceof HTMLLabelElement &&
      checkbox instanceof HTMLInputElement &&
      !trigger.hasAttribute('aria-expanded')
    ) {
      trigger.setAttribute('aria-expanded', checkbox.checked ? 'true' : 'false');
    }
  });
}

/**
 * @param {HTMLElement} facetsSource
 * @param {HTMLElement} listWrap
 * @param {HTMLElement} rootNav
 * @param {HTMLElement} panesMount
 */
function layoutSearchFacetsDesktop(
  facetsSource,
  listWrap,
  rootNav,
  panesMount,
) {
  panesMount
    .querySelectorAll('.ais-facet-dropdown-wrapper')
    .forEach((wrapper) => {
      if (wrapper instanceof HTMLElement) {
        facetsSource.appendChild(wrapper);
      }
    });

  rootNav.innerHTML = '';
  panesMount.innerHTML = '';

  ensureDesktopFacetPlacement(facetsSource, listWrap);
}

/**
 * @param {HTMLElement} facetsSource
 * @param {HTMLElement} listWrap
 * @param {HTMLElement} rootNav
 * @param {HTMLElement} panesMount
 */
function layoutSearchFacetsMobile(
  facetsSource,
  listWrap,
  rootNav,
  panesMount,
) {
  facetsSource.classList.remove('wf-search__facets-source--desktop');
  facetsSource.classList.add('wf-search__facets-source--drawer');

  rootNav.innerHTML = '';
  panesMount.innerHTML = '';

  if (listWrap.contains(facetsSource)) {
    const page = facetsSource.closest('.ais-page');
    const anchor = page?.querySelector('.ais-facets-button');
    if (page instanceof HTMLElement) {
      if (anchor instanceof HTMLElement) {
        anchor.insertAdjacentElement('afterend', facetsSource);
      } else {
        page.appendChild(facetsSource);
      }
    }
  }

  const wrappers = [
    ...facetsSource.querySelectorAll('.ais-facet-dropdown-wrapper'),
  ].filter((node) => node instanceof HTMLElement);

  wrappers.forEach((wrapper, index) => {
    const paneId = `wf-search-facet-pane-${index}`;
    const title =
      wrapper.querySelector('.ais-facet--header .facets__label')?.textContent?.trim() ??
      wrapper.querySelector('.ais-facet--header')?.textContent?.trim() ??
      'Filter';

    rootNav.appendChild(buildMobileDrilldownRootItem(title, paneId));

    const pane = buildMobileDrilldownPaneHeader(wrapper, paneId);
    panesMount.appendChild(pane);
  });

  appendMobileSortDrilldown(facetsSource.closest('.ais-page'), rootNav, panesMount);
}

/**
 * A root row as the PLP renders it: the row wrapped in a root-item that
 * carries the 24px gutter, label left, optional status and the gold arrow
 * right.
 *
 * @param {string} title
 * @param {string} paneId
 * @param {string} [status]
 * @returns {HTMLElement}
 */
function buildMobileDrilldownRootItem(title, paneId, status) {
  const item = document.createElement('div');
  item.className = 'facets-drawer-drilldown__root-item';
  item.setAttribute('role', 'presentation');
  item.innerHTML = `
    <button type="button" class="facets-drawer-drilldown__root-row button-unstyled" data-filter-drilldown-target="${paneId}" aria-expanded="false">
      <span class="facets-drawer-drilldown__root-label facets__label">${title}</span>
      <div class="facets-drawer-drilldown__root-meta">
        ${
          typeof status === 'string'
            ? `<span class="facets-drawer-drilldown__sort-status wf-search__drawer-sort-status">${status}</span>`
            : ''
        }
        <span class="svg-wrapper facets-drawer-drilldown__root-arrow" aria-hidden="true">${SEARCH_DRILLDOWN_NAV_ARROW_SVG}</span>
      </div>
    </button>
  `;
  return item;
}

/**
 * Sort is the drawer's last row on the PLP — "Sort   Date, new to old →" —
 * opening its own pane, rather than a control pinned above the filters.
 *
 * @param {Element | null} page
 * @param {HTMLElement} rootNav
 * @param {HTMLElement} panesMount
 */
function appendMobileSortDrilldown(page, rootNav, panesMount) {
  if (!(page instanceof HTMLElement)) return;
  const options = getSearchSortOptions(page);
  if (options.length === 0) return;

  const label = window.Theme?.translations?.search_sort_label ?? 'Sort';
  const paneId = 'wf-search-facet-pane-sort';
  const current = options.find((option) => option.selected)?.label ?? '';

  rootNav.appendChild(buildMobileDrilldownRootItem(label, paneId, current));

  const pane = document.createElement('div');
  pane.id = paneId;
  pane.className = 'facets-drawer-drilldown__pane wf-search__drawer-pane';
  pane.setAttribute('aria-hidden', 'true');
  pane.innerHTML = `
    <header class="facets-drawer-drilldown__pane-header wf-search__drawer-pane-header">
      <div class="facets-drawer-drilldown__pane-header-main">
        <button type="button" class="facets-drawer-drilldown__pane-back button-unstyled" data-filter-drilldown-back aria-label="${window.Theme?.translations?.search_back_label ?? 'Back'}">
          <span class="svg-wrapper" aria-hidden="true">${SEARCH_DRILLDOWN_BACK_SVG}</span>
        </button>
        <span class="facets-drawer-drilldown__pane-title">${label}</span>
      </div>
    </header>
    <div class="facets-drawer-drilldown__pane-body wf-search__drawer-pane-body">
      <div class="wf-search__sort-options wf-search__drawer-sort-options" role="listbox" aria-label="${label}"></div>
    </div>
  `;
  panesMount.appendChild(pane);

  const list = pane.querySelector('.wf-search__drawer-sort-options');
  if (list instanceof HTMLElement) renderSearchSortOptionList(list, options);
}

/**
 * @param {HTMLElement} page
 */
function applySearchFacetLayout(page) {
  if (!wfSearchInstantSearchReady || !searchFacetWidgetsAreMounted()) return;

  const facetsSource = page.querySelector('.wf-search__facets-source');
  const listWrap = page.querySelector('.wf-search__facets-list-wrap');
  const rootNav = page.querySelector('.wf-search__drilldown-root-nav');
  const panesMount = page.querySelector('.wf-search__drilldown-panes');

  if (
    !(facetsSource instanceof HTMLElement) ||
    !(listWrap instanceof HTMLElement) ||
    !(rootNav instanceof HTMLElement) ||
    !(panesMount instanceof HTMLElement)
  ) {
    return;
  }

  const mode = WF_SEARCH_DESKTOP_MQ.matches ? 'desktop' : 'mobile';

  if (mode === 'desktop') {
    if (mode !== wfSearchFacetLayoutMode) {
      wfSearchFacetLayoutMode = mode;
      layoutSearchFacetsDesktop(facetsSource, listWrap, rootNav, panesMount);
    } else {
      ensureDesktopFacetPlacement(facetsSource, listWrap);
    }
    return;
  }

  if (mode === wfSearchFacetLayoutMode) return;

  wfSearchFacetLayoutMode = mode;
  layoutSearchFacetsMobile(facetsSource, listWrap, rootNav, panesMount);
}

/** @param {HTMLElement} page */
function positionSearchSort(page) {
  const sort = page.querySelector('.wf-search__sort');
  const toolbar = page.querySelector('.wf-search__facets-toolbar');

  if (!(sort instanceof HTMLElement)) return;

  // Desktop: Sort sits at the end of the sand bar. Mobile reaches it through
  // the drawer's Sort row instead, so the element can stay wherever it is;
  // its hidden Algolia <select> keeps working from there.
  if (
    WF_SEARCH_DESKTOP_MQ.matches &&
    toolbar instanceof HTMLElement &&
    sort.parentElement !== toolbar
  ) {
    toolbar.appendChild(sort);
  }
}

/**
 * Algolia names its sort orders "Sort by Newest", "Sort by Popularity"… Under a
 * trigger that already says Sort, the prefix is noise, and the PLP's own list
 * ("Price, low to high") is sentence case.
 *
 * @param {string} label
 * @returns {string}
 */
function formatSearchSortOptionLabel(label) {
  const text = String(label ?? '')
    .trim()
    .replace(/^sort by\s+/i, '');
  if (!text) return text;
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** @param {HTMLElement} page */
function getSearchSortSelect(page) {
  const select = page.querySelector('.wf-search__sort-orders .ais-SortBy-select');
  return select instanceof HTMLSelectElement ? select : null;
}

/**
 * @param {HTMLElement} page
 * @returns {Array<{ value: string, label: string, selected: boolean }>}
 */
function getSearchSortOptions(page) {
  const select = getSearchSortSelect(page);
  if (!select) return [];

  return [...select.options].map((option) => ({
    value: option.value,
    label: formatSearchSortOptionLabel(option.textContent ?? ''),
    selected: option.value === select.value,
  }));
}

/**
 * Route a choice through Algolia's own <select>, so the SortBy widget stays
 * the source of truth for the index (and the URL state that follows it).
 *
 * @param {HTMLElement} page
 * @param {string} value
 */
function applySearchSortValue(page, value) {
  const select = getSearchSortSelect(page);
  if (!select || select.value === value) return;
  select.value = value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * @param {HTMLElement} container
 * @param {Array<{ value: string, label: string, selected: boolean }>} options
 */
function renderSearchSortOptionList(container, options) {
  const signature = options
    .map((option) => `${option.value}:${option.selected ? 1 : 0}`)
    .join('|');
  if (container.dataset.sortSignature === signature) return;
  container.dataset.sortSignature = signature;

  container.innerHTML = options
    .map(
      (option) => `
        <button
          type="button"
          class="wf-search__sort-option${option.selected ? ' is-selected' : ''}"
          role="option"
          aria-selected="${option.selected ? 'true' : 'false'}"
          data-sort-value="${option.value}"
        >
          <span class="svg-wrapper wf-search__sort-checkmark">${SEARCH_CHECKMARK_SVG}</span>
          <span class="wf-search__sort-option-label">${option.label}</span>
        </button>
      `,
    )
    .join('');
}

/**
 * Desktop Sort: the PLP's "Sort ⌄" summary with a white popover of options,
 * in place of Algolia's native "Sorted by RELEVANCE" select.
 *
 * @param {HTMLElement} page
 */
function syncSearchSortMenu(page) {
  const sort = page.querySelector('.wf-search__sort');
  if (!(sort instanceof HTMLElement)) return;

  const options = getSearchSortOptions(page);
  let menu = sort.querySelector('.wf-search__sort-menu');

  if (options.length === 0) {
    menu?.remove();
    return;
  }

  if (!(menu instanceof HTMLElement)) {
    const label = window.Theme?.translations?.search_sort_label ?? 'Sort';
    menu = document.createElement('div');
    menu.className = 'wf-search__sort-menu';
    menu.innerHTML = `
      <button type="button" class="facets__summary wf-search__sort-trigger" aria-haspopup="listbox" aria-expanded="false">
        <span class="facets__label">${label}</span>
        <span class="svg-wrapper icon-caret icon-animated" aria-hidden="true">${SEARCH_CARET_SVG}</span>
      </button>
      <div class="wf-search__sort-panel" role="listbox" aria-label="${label}">
        <div class="wf-search__sort-options"></div>
      </div>
    `;
    sort.appendChild(menu);
  }

  const list = menu.querySelector('.wf-search__sort-options');
  if (list instanceof HTMLElement) {
    renderSearchSortOptionList(list, options);
  }

  // The drawer is laid out once per breakpoint; if the SortBy widget mounted
  // after that, add its row now.
  if (
    wfSearchFacetLayoutMode === 'mobile' &&
    !page.querySelector('#wf-search-facet-pane-sort')
  ) {
    const rootNav = page.querySelector('.wf-search__drilldown-root-nav');
    const panesMount = page.querySelector('.wf-search__drilldown-panes');
    if (rootNav instanceof HTMLElement && panesMount instanceof HTMLElement) {
      appendMobileSortDrilldown(page, rootNav, panesMount);
    }
  }

  const drawerList = page.querySelector('.wf-search__drawer-sort-options');
  if (drawerList instanceof HTMLElement) {
    renderSearchSortOptionList(drawerList, options);
  }

  const status = page.querySelector('.wf-search__drawer-sort-status');
  if (status instanceof HTMLElement) {
    status.textContent = options.find((option) => option.selected)?.label ?? '';
  }
}

/**
 * @param {HTMLElement} page
 * @param {boolean} open
 */
function setSearchSortMenuOpen(page, open) {
  const menu = page.querySelector('.wf-search__sort-menu');
  if (!(menu instanceof HTMLElement)) return;
  menu.classList.toggle('is-open', open);
  menu
    .querySelector('.wf-search__sort-trigger')
    ?.setAttribute('aria-expanded', open ? 'true' : 'false');
}

/**
 * @param {HTMLElement} page
 */
function enhanceSearchFacetPanels(page) {
  page.querySelectorAll('.ais-RefinementList-list, .ais-facet .ais-Panel-body > div > ul').forEach(
    (list) => {
      if (!(list instanceof HTMLElement)) return;
      list.classList.add(
        'facets__inputs-list',
        'list-unstyled',
        'wf-search__facet-values',
      );
    },
  );

  page.querySelectorAll('.ais-facet-dropdown-container').forEach((panel) => {
    if (!(panel instanceof HTMLElement)) return;
    panel.classList.add('details-content', 'wf-search__facet-panel-inner');
  });
}

/**
 * @param {HTMLElement} page
 */
function openSearchFiltersDrawer(page) {
  const dialog = page.querySelector('#wf-search-filters-drawer dialog');
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

  wfSearchDrawerScrollY = window.scrollY;
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${wfSearchDrawerScrollY}px`;
  dialog.showModal();
}

/**
 * @param {HTMLElement} page
 */
function closeSearchFiltersDrawer(page) {
  const dialog = page.querySelector('#wf-search-filters-drawer dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  dialog.close();
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';

  if (typeof wfSearchDrawerScrollY === 'number') {
    window.scrollTo(0, wfSearchDrawerScrollY);
    wfSearchDrawerScrollY = null;
  }
}

/**
 * @param {HTMLElement} page
 */
function syncSearchFacetUi(page) {
  injectSearchFacetChrome(page);
  ensureSearchFacetChromePosition(page);
  updateSearchProductCounts(page);
  applySearchFacetLayout(page);
  positionSearchSort(page);
  syncSearchSortMenu(page);
  enhanceSearchFacetPanels(page);

  const sourceRefined = page.querySelector('.wf-search__current-refined');
  const sourceClear = page.querySelector('.wf-search__clear-refinements');
  const chipsMount = page.querySelector('.wf-search__refinement-chips');
  const clearMount = page.querySelector('.wf-search__clear-all-wrap');

  if (
    sourceRefined instanceof HTMLElement &&
    sourceClear instanceof HTMLElement &&
    chipsMount instanceof HTMLElement &&
    clearMount instanceof HTMLElement
  ) {
    syncSearchRefinementChips(
      chipsMount,
      clearMount,
      sourceRefined,
      sourceClear,
    );
  }
}

/**
 * @param {HTMLElement} page
 */
function bindSearchFacetControls(page) {
  if (page.dataset.wfSearchFacetControlsBound === 'true') return;
  page.dataset.wfSearchFacetControlsBound = 'true';

  page.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-wf-search-open-filters]')) {
      openSearchFiltersDrawer(page);
      return;
    }

    if (target.closest('[data-wf-search-close-filters]')) {
      closeSearchFiltersDrawer(page);
      return;
    }

    if (target.closest('.wf-search__drawer-clear-all')) {
      page
        .querySelector(
          '.wf-search__clear-refinements .ais-ClearRefinements-button:not(.ais-ClearRefinements-button--disabled)',
        )
        ?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      return;
    }

    const sortOption = target.closest('.wf-search__sort-option');
    if (sortOption instanceof HTMLElement) {
      setSearchSortMenuOpen(page, false);
      applySearchSortValue(page, sortOption.dataset.sortValue ?? '');
      return;
    }

    const sortTrigger = target.closest('.wf-search__sort-trigger');
    if (sortTrigger instanceof HTMLElement) {
      const menu = sortTrigger.closest('.wf-search__sort-menu');
      setSearchSortMenuOpen(page, !menu?.classList.contains('is-open'));
      return;
    }

    const facetTrigger = target.closest('.wf-search__facet-trigger');
    if (facetTrigger instanceof HTMLLabelElement && WF_SEARCH_DESKTOP_MQ.matches) {
      const wrapper = facetTrigger.closest('.ais-facet-dropdown-wrapper');
      const checkbox = wrapper?.querySelector('.ais-dropdown-checkbox');
      if (!(checkbox instanceof HTMLInputElement)) return;

      event.preventDefault();

      const willOpen = !checkbox.checked;

      page.querySelectorAll('.ais-dropdown-checkbox').forEach((other) => {
        if (!(other instanceof HTMLInputElement)) return;
        other.checked = false;
        const otherTrigger = other
          .closest('.ais-facet-dropdown-wrapper')
          ?.querySelector('.wf-search__facet-trigger');
        if (otherTrigger instanceof HTMLLabelElement) {
          otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });

      checkbox.checked = willOpen;
      facetTrigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    }
  });

  document.addEventListener('click', (event) => {
    if (!WF_SEARCH_DESKTOP_MQ.matches) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !page.contains(target)) return;
    if (!target.closest('.wf-search__sort-menu')) {
      setSearchSortMenuOpen(page, false);
    }
    if (target.closest('.wf-search__facet-item')) return;

    page.querySelectorAll('.ais-dropdown-checkbox:checked').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        const trigger = input
          .closest('.ais-facet-dropdown-wrapper')
          ?.querySelector('.wf-search__facet-trigger');
        if (trigger instanceof HTMLLabelElement) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = page.querySelector('.wf-search__sort-menu.is-open');
    if (!(menu instanceof HTMLElement)) return;
    setSearchSortMenuOpen(page, false);
    const trigger = menu.querySelector('.wf-search__sort-trigger');
    if (trigger instanceof HTMLElement) trigger.focus();
  });

  if (typeof WF_SEARCH_DESKTOP_MQ.addEventListener === 'function') {
    WF_SEARCH_DESKTOP_MQ.addEventListener('change', () => {
      wfSearchFacetLayoutMode = null;
      syncSearchFacetUi(page);
    });
  } else {
    WF_SEARCH_DESKTOP_MQ.addListener(() => {
      wfSearchFacetLayoutMode = null;
      syncSearchFacetUi(page);
    });
  }
}

function initSearchFacetsUi() {
  const page = document.querySelector('.ais-page.wf-search');
  if (!(page instanceof HTMLElement)) return;

  injectSearchFacetChrome(page);
  bindSearchFacetControls(page);
  syncSearchFacetUi(page);
}

function setupSearchFacetsListeners() {
  if (wfSearchFacetsInitialized) return;
  wfSearchFacetsInitialized = true;

  document.addEventListener('algolia.instantSearch.afterInit', () => {
    wfSearchInstantSearchReady = true;
    wfSearchFacetLayoutMode = null;
    initSearchFacetsUi();
  });

  document.addEventListener('algolia.instantSearch.onRender', () => {
    wfSearchInstantSearchReady = true;
    initSearchFacetsUi();
  });
}

setupSearchFacetsListeners();

document.addEventListener('DOMContentLoaded', initSearchFacetsUi);
